import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Stepper, Step, StepLabel,
  TextField, MenuItem, Chip
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { ContentCut, Spa, Brush, Face, HotTub } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ClientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const BookAppointment: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedServices, setSelectedServices] = useState<any[]>([]);
  const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);
  const [appointmentTime, setAppointmentTime] = useState<Date | null>(null);
  const [availableStaff, setAvailableStaff] = useState<any[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [allServices, setAllServices] = useState<any[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  const steps = !user ? 
    ['Select Services', 'Choose Staff & Time', 'Your Info', 'Confirm Booking'] :
    (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') ?
    ['Select Client', 'Select Services', 'Choose Staff & Time', 'Confirm Booking'] :
    ['Select Services', 'Choose Staff & Time', 'Confirm Booking'];

  useEffect(() => {
    // Fetch clients for admin users
    if (user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST')) {
      fetchClients();
    }
  }, [user]);
  
  const fetchClients = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/clients');
      if (response.ok) {
        const clientsData = await response.json();
        setClients(clientsData);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchServices = async () => {
    // Services are now defined inline to match specialties
    setAllServices([]);
  };

  const [serviceCategories, setServiceCategories] = useState<any[]>([]);

  useEffect(() => {
    fetchServicesAndCategories();
  }, []);

  const fetchServicesAndCategories = async () => {
    try {
      const [servicesResponse, categoriesResponse] = await Promise.all([
        fetch('https://salon.rockyroadsolutions.com/api/services'),
        fetch('https://salon.rockyroadsolutions.com/api/services/categories')
      ]);
      
      if (servicesResponse.ok && categoriesResponse.ok) {
        const services = await servicesResponse.json();
        const categories = await categoriesResponse.json();
        
        const categoryIcons = {
          'Hair': <ContentCut />,
          'Makeup': <Face />,
          'Skin': <Spa />,
          'Nails': <Brush />,
          'Spa': <HotTub />
        };
        
        const categoryColors = {
          'Hair': '#E91E63',
          'Makeup': '#9C27B0', 
          'Skin': '#FF5722',
          'Nails': '#00BCD4',
          'Spa': '#4CAF50'
        };
        
        const formattedCategories = categories.map((category: any) => ({
          name: category.name,
          icon: categoryIcons[category.name as keyof typeof categoryIcons] || <ContentCut />,
          color: categoryColors[category.name as keyof typeof categoryColors] || '#666',
          services: services.filter((service: any) => service.category === category.name)
        }));
        
        setServiceCategories(formattedCategories);
      }
    } catch (error) {
      console.error('Error fetching services and categories:', error);
    }
  };

  const handleServiceToggle = (service: any, category: string) => {
    const serviceWithCategory = { ...service, category };
    const exists = selectedServices.find(s => s.name === service.name);
    
    if (exists) {
      setSelectedServices(selectedServices.filter(s => s.name !== service.name));
    } else {
      setSelectedServices([...selectedServices, serviceWithCategory]);
    }
  };

  const totalPrice = selectedServices.reduce((sum, service) => sum + parseFloat(service.price), 0);
  const totalDuration = selectedServices.reduce((sum, service) => sum + parseInt(service.duration), 0);

  const fetchAvailableStaff = async (date: Date) => {
    try {
      // Fetch all active staff instead of date-specific availability
      const response = await fetch('https://salon.rockyroadsolutions.com/api/staff');
      if (response.ok) {
        const allStaff = await response.json();
        // Filter for active stylists and therapists
        const availableStaff = allStaff.filter((staff: any) => 
          staff.status === 'Active' && (staff.role === 'stylist' || staff.role === 'therapist')
        );
        console.log('Available staff for booking:', availableStaff);
        console.log('All staff from API:', allStaff);
        setAvailableStaff(availableStaff);
      }
    } catch (error) {
      console.error('Error fetching available staff:', error);
    }
  };

  const [allTimeSlots, setAllTimeSlots] = useState<{time: string, available: boolean}[]>([]);
  
  const fetchAvailableTimeSlots = async (date: Date, staffId: number) => {
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/available-slots/${dateStr}/${staffId}`);
      if (response.ok) {
        const slotsWithStatus = await response.json();
        setAllTimeSlots(slotsWithStatus);
        
        // Extract available slots for backward compatibility
        const availableSlots = slotsWithStatus.filter((slot: any) => slot.available).map((slot: any) => slot.time);
        setAvailableTimeSlots(availableSlots);
      }
    } catch (error) {
      console.error('Error fetching available time slots:', error);
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleBookingConfirm = async () => {
    try {
      let clientId = selectedClient?.id;
      
      // If admin is creating new client, create client first
      if (user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && !selectedClient) {
        const clientResponse = await fetch('https://salon.rockyroadsolutions.com/api/clients', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: clientInfo.firstName,
            lastName: clientInfo.lastName,
            email: clientInfo.email,
            phone: clientInfo.phone
          })
        });
        
        if (clientResponse.ok) {
          const newClient = await clientResponse.json();
          clientId = newClient.id;
        } else {
          alert('Failed to create client. Please try again.');
          return;
        }
      }
      
      // For logged in clients, use their own ID
      if (user && user.role === 'CLIENT') {
        clientId = user.id;
      }
      
      const endpoint = user ? 'https://salon.rockyroadsolutions.com/api/appointments' : 'https://salon.rockyroadsolutions.com/api/public/book-appointment';
      
      const formatLocalDate = (date: Date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      };

      const appointmentData = user ? {
        clientId,
        staffId: selectedStaff.id,
        appointmentDate: appointmentDate ? formatLocalDate(appointmentDate) : null,
        startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        totalDuration,
        totalPrice,
        notes: '',
        services: selectedServices,
        staff: selectedStaff
      } : {
        firstName: clientInfo.firstName,
        lastName: clientInfo.lastName,
        email: clientInfo.email,
        phone: clientInfo.phone,
        staffId: selectedStaff.id,
        appointmentDate: appointmentDate ? formatLocalDate(appointmentDate) : null,
        startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        totalDuration,
        totalPrice,
        services: selectedServices,
        staff: selectedStaff
      };

      const apiData = user ? {
        clientId,
        staffId: selectedStaff.id,
        appointmentDate: appointmentDate ? formatLocalDate(appointmentDate) : null,
        startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        totalDuration,
        totalPrice,
        notes: '',
        services: selectedServices.map(service => ({
          serviceId: service.id,
          staffId: selectedStaff.id,
          startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
          endTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
          price: service.price
        }))
      } : {
        firstName: clientInfo.firstName,
        lastName: clientInfo.lastName,
        email: clientInfo.email,
        phone: clientInfo.phone,
        staffId: selectedStaff.id,
        appointmentDate: appointmentDate ? formatLocalDate(appointmentDate) : null,
        startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        totalDuration,
        totalPrice,
        services: selectedServices.map(service => ({
          serviceId: service.id,
          staffId: selectedStaff.id,
          startTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
          endTime: appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
          price: service.price
        }))
      };

      console.log('Sending appointment data:', apiData);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });
      
      console.log('Response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Booking successful:', result);
        
        // Navigate to payment page with appointment data
        navigate('/payment', { 
          state: { 
            appointmentData: {
              ...appointmentData,
              id: result.appointmentId || result.id
            } 
          } 
        });
      } else {
        try {
          const errorText = await response.text();
          console.error('Booking failed response:', errorText);
        } catch (e) {
          console.error('Could not parse error response');
        }
        alert('Booking failed. Please try again.');
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Booking failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        background: `
          linear-gradient(135deg, rgba(255, 248, 245, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%),
          url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        p: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Typography variant="h4" fontWeight={600} gutterBottom textAlign="center">
        Book Your Beauty Appointment ✨
      </Typography>

      <Card sx={{ maxWidth: 1000, mx: 'auto', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Client
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(client) => `${client.first_name || client.firstName} ${client.last_name || client.lastName} - ${client.email}`}
                    value={selectedClient}
                    onChange={(event, newValue) => {
                      setSelectedClient(newValue);
                      if (newValue) {
                        setClientInfo({
                          firstName: newValue.first_name || newValue.firstName,
                          lastName: newValue.last_name || newValue.lastName,
                          email: newValue.email,
                          phone: newValue.phone
                        });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search and Select Client"
                        placeholder="Type to search clients..."
                        sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(client => {
                        const fullName = `${client.first_name || client.firstName} ${client.last_name || client.lastName}`;
                        const searchText = inputValue.toLowerCase();
                        return fullName.toLowerCase().includes(searchText) || 
                               client.email.toLowerCase().includes(searchText) ||
                               (client.phone && client.phone.includes(searchText));
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                    Or create new client:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={clientInfo.firstName}
                    onChange={(e) => {
                      setClientInfo({...clientInfo, firstName: e.target.value});
                      setSelectedClient(null);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={clientInfo.lastName}
                    onChange={(e) => {
                      setClientInfo({...clientInfo, lastName: e.target.value});
                      setSelectedClient(null);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={clientInfo.email}
                    onChange={(e) => {
                      setClientInfo({...clientInfo, email: e.target.value});
                      setSelectedClient(null);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={clientInfo.phone}
                    onChange={(e) => {
                      setClientInfo({...clientInfo, phone: e.target.value});
                      setSelectedClient(null);
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {!user && activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Your Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    required
                    value={clientInfo.firstName}
                    onChange={(e) => setClientInfo({...clientInfo, firstName: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    required
                    value={clientInfo.lastName}
                    onChange={(e) => setClientInfo({...clientInfo, lastName: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    required
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo({...clientInfo, email: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    required
                    value={clientInfo.phone}
                    onChange={(e) => setClientInfo({...clientInfo, phone: e.target.value})}
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#E91E63' } } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {((user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 1) || 
            (user && user.role === 'CLIENT' && activeStep === 0) || 
            (!user && activeStep === 0)) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Choose Your Services
              </Typography>
              {serviceCategories.map((category) => (
                <Box key={category.name} sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {category.icon}
                    {category.name}
                  </Typography>
                  <Grid container spacing={2}>
                    {category.services.map((service: any) => (
                      <Grid item xs={12} sm={6} key={service.name}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            border: selectedServices.find(s => s.name === service.name) ? '2px solid #E91E63' : '1px solid #E0E0E0',
                            transition: 'all 0.3s ease',
                            '&:hover': { transform: 'translateY(-2px)' }
                          }}
                          onClick={() => handleServiceToggle(service, category.name)}
                        >
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {service.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {service.duration} minutes
                            </Typography>
                            <Typography variant="h6" color="primary">
                              ${service.price}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
              
              {selectedServices.length > 0 && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Selected Services:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {selectedServices.map((service) => (
                      <Chip
                        key={service.name}
                        label={`${service.name} - $${service.price}`}
                        onDelete={() => handleServiceToggle(service, service.category)}
                        color="primary"
                      />
                    ))}
                  </Box>
                  <Typography variant="body1">
                    Total: ${totalPrice} • Duration: {totalDuration} minutes
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {((user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 2) || 
            (user && user.role === 'CLIENT' && activeStep === 1) || 
            (!user && activeStep === 1)) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Select Staff & Schedule
              </Typography>
              
              {/* Date Selection */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <DatePicker
                    label="Appointment Date"
                    value={appointmentDate}
                    onChange={(date) => {
                      setAppointmentDate(date);
                      if (date) {
                        fetchAvailableStaff(date);
                        // If staff is already selected, refresh their time slots for the new date
                        if (selectedStaff) {
                          fetchAvailableTimeSlots(date, selectedStaff.id);
                        }
                      }
                    }}
                    slotProps={{ textField: { fullWidth: true } }}
                    minDate={new Date()}
                  />
                </Grid>
              </Grid>

              {/* Staff Selection */}
              {appointmentDate && availableStaff.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Available Staff
                  </Typography>
                  <Grid container spacing={2}>
                    {availableStaff.map((staff) => (
                      <Grid item xs={12} sm={6} key={staff.id}>
                        <Card
                          sx={{
                            cursor: 'pointer',
                            border: selectedStaff?.id === staff.id ? '2px solid #E91E63' : '1px solid #E0E0E0',
                            transition: 'all 0.3s ease',
                            '&:hover': { transform: 'translateY(-2px)' }
                          }}
                          onClick={() => {
                            setSelectedStaff(staff);
                            fetchAvailableTimeSlots(appointmentDate, staff.id);
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                              {staff.firstName} {staff.lastName}
                            </Typography>
                            <Chip
                              label={staff.role}
                              color="primary"
                              size="small"
                              sx={{ mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Specializes in: {staff.specialties?.join(', ') || 'All Services'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Time Slot Selection */}
              {selectedStaff && availableTimeSlots.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Available Time Slots
                  </Typography>
                  <Grid container spacing={1}>
                    {allTimeSlots.map((slot) => (
                      <Grid item xs={6} sm={4} md={3} key={slot.time}>
                        <Button
                          fullWidth
                          variant={appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) === slot.time ? 'contained' : 'outlined'}
                          disabled={!slot.available}
                          onClick={() => {
                            if (slot.available) {
                              const [hours, minutes] = slot.time.split(':');
                              const time = new Date();
                              time.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                              setAppointmentTime(time);
                            }
                          }}
                          sx={{ 
                            minHeight: 48,
                            opacity: slot.available ? 1 : 0.5,
                            backgroundColor: !slot.available ? '#f5f5f5' : undefined,
                            color: !slot.available ? '#999' : undefined,
                            '&:hover': {
                              backgroundColor: !slot.available ? '#f5f5f5' : undefined
                            }
                          }}
                        >
                          {slot.time}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {((user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 3) || 
            (user && user.role === 'CLIENT' && activeStep === 2) || 
            (!user && activeStep === 3)) && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Confirm Your Booking
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Date:</strong> {appointmentDate?.toDateString()}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Time:</strong> {appointmentTime?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Staff:</strong> {selectedStaff?.firstName} {selectedStaff?.lastName} ({selectedStaff?.role})
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Services:</strong>
                </Typography>
                {selectedServices.map((service) => (
                  <Typography key={service.name} variant="body2" sx={{ ml: 2 }}>
                    • {service.name} - ${service.price} ({service.duration} min)
                  </Typography>
                ))}
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Total: ${totalPrice}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={activeStep === steps.length - 1 ? handleBookingConfirm : handleNext}
              disabled={
                (user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 0 && (!clientInfo.firstName || !clientInfo.lastName || !clientInfo.email || !clientInfo.phone)) ||
                (!user && activeStep === 2 && (!clientInfo.firstName || !clientInfo.lastName || !clientInfo.email || !clientInfo.phone)) ||
                (((user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 1) || (user && user.role === 'CLIENT' && activeStep === 0) || (!user && activeStep === 0)) && selectedServices.length === 0) ||
                (((user && (user.role === 'ADMIN' || user.role === 'RECEPTIONIST') && activeStep === 2) || (user && user.role === 'CLIENT' && activeStep === 1) || (!user && activeStep === 1)) && (!appointmentDate || !appointmentTime || !selectedStaff))
              }
            >
              {activeStep === steps.length - 1 ? 'Confirm Booking' : 'Next'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookAppointment;
